// Pure helpers for turning a raw BSE StAR MF 2.0 response envelope into something a
// screen can render as structure instead of a JSON dump. No JSX here — see
// components/bse/BseWorkbenchResult.js for the presentational half.
//
// These helpers are display-only conveniences (column choice, label wording, row
// identity). They must never be the place business rules about BSE's message
// catalogue live — that stays in bse_v2/errors.py + bse_v2/envelope.py on the
// backend, which already attaches a rendered `outcome` and `messages` to every
// workbench response (see _bse_passthrough in routes_bse2.py). This file only
// shapes the parts the backend deliberately leaves to the screen: `data`.

export function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

// `out` from the backend's passthrough envelope is either the BSE body itself
// (success) or an error dict whose real BSE body, if BSE sent one, lives one
// level down under `.response` (mirrors bse_v2.client.BSEV2Error.to_dict()).
// Returns null when no BSE-shaped body is available at all (e.g. a network
// failure before BSE ever replied).
export function extractBody(response) {
  if (!isPlainObject(response)) return null;
  if ("messages" in response || "data" in response || "status" in response) return response;
  if (isPlainObject(response.response)) return response.response;
  return null;
}

// BSE's list envelopes nest rows under one of a few conventional keys
// (`lists` is the most common; `items`/`orders`/`list` appear on some
// endpoints). Falls back to a bare array. Returns null when `data` is not a
// list-shaped value at all, so the caller can render it as a single record.
export function rowsFromData(data) {
  if (Array.isArray(data)) return data;
  if (isPlainObject(data)) {
    for (const key of ["lists", "items", "orders", "list"]) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return null;
}

// Short BSE wire-field tokens that read better expanded to a whole word than
// left as a fragment. Deliberately small and forgiving: a token missing from
// here just falls back to Title Case, and the raw key is always shown
// alongside the humanized label, so an imperfect guess here never hides
// information — unlike the error catalogue, this list carries no obligation
// to be complete or exact.
const WORD_EXPANSIONS = {
  exch: "Exchange", mem: "Member", reg: "Registration", acct: "Account", num: "Number",
  no: "No.", amt: "Amount", dt: "Date", txn: "Transaction", mob: "Mobile", qty: "Quantity",
  addr: "Address", min: "Minimum", max: "Maximum", cur: "Currency", src: "Source",
  dest: "Destination", freq: "Frequency", ninstallments: "Installments",
};

const ACRONYMS = new Set([
  "ucc", "ifsc", "pan", "kyc", "nft", "sxp", "dp", "rta", "pg", "utr", "bse", "nach",
  "sip", "stp", "swp", "amc", "id", "2fa", "upi", "isin", "tat", "idcw", "nav", "euin",
  "arn", "tds", "gst", "tan", "vpa", "umrn", "dpc",
]);

export function humanizeKey(key) {
  if (key === null || key === undefined || key === "") return "";
  const tokens = String(key).replace(/^_+/, "").split(/[_\s]+/).filter(Boolean);
  if (tokens.length === 0) return String(key);
  return tokens
    .map((token) => {
      const lower = token.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      if (WORD_EXPANSIONS[lower]) return WORD_EXPANSIONS[lower];
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(" ");
}

// Field names likely to matter most to an operator scanning a list, checked
// only when there are more columns than MAX_AUTO_COLUMNS. Generic BSE
// conventions (id-like, status, ucc, scheme, amount...), never a specific
// endpoint's field list — an unmatched shape just falls through to natural
// column order, it never loses columns because a pattern here missed it.
const PRIORITY_KEY_PATTERNS = [
  /^id$/i, /_id$/i, /^status$/i, /ucc/i, /client_code/i, /scheme/i, /^amount$/i,
  /^cur$/i, /reg_no/i, /folio/i, /^type$/i, /_at$/i, /date/i,
];
export const MAX_AUTO_COLUMNS = 12;

// Column order for a table of BSE rows: every key that appears on any row,
// primitive-valued keys first (in the order they first appear), then
// nested/object-valued keys. When that would produce more columns than
// MAX_AUTO_COLUMNS, the priority patterns above are pulled to the front
// before truncating, so the columns most likely to matter (status, id,
// amount...) survive the cut rather than whichever happened to appear first.
export function chooseColumns(rows) {
  const primitive = [];
  const rest = [];
  const seen = new Set();
  rows.forEach((row) => {
    if (!isPlainObject(row)) return;
    Object.keys(row).forEach((key) => {
      if (seen.has(key)) return;
      seen.add(key);
      (typeof row[key] !== "object" || row[key] === null ? primitive : rest).push(key);
    });
  });
  const ordered = [...primitive, ...rest];
  if (ordered.length <= MAX_AUTO_COLUMNS) return ordered;
  const priority = ordered.filter((key) => PRIORITY_KEY_PATTERNS.some((pattern) => pattern.test(key)));
  const remainder = ordered.filter((key) => !priority.includes(key));
  return [...priority, ...remainder].slice(0, MAX_AUTO_COLUMNS);
}

// Pretty-prints a nested object/array as indented `Label: value` lines, for
// the collapsed-by-default expander behind a table cell or detail row. Pure
// recursion over data (not a recursive JSX component — this project's dev
// build pipeline (craco.config.js -> @emergentbase/visual-edits) applies a
// babel plugin in every non-production build that cannot handle a JSX
// component that renders itself, self- or mutually-recursive; keeping the
// recursion here, in a plain string-returning function, avoids that
// entirely while still handling arbitrarily deep BSE payloads.
function nestedLine(pad, label, entryValue, indent) {
  const hasNestedContent = entryValue !== null && typeof entryValue === "object"
    && (Array.isArray(entryValue) ? entryValue.length : Object.keys(entryValue).length) > 0;
  if (hasNestedContent) return `${pad}${label}\n${humanizeNested(entryValue, indent + 1)}`;
  return `${pad}${label} ${humanizeNested(entryValue, indent)}`;
}

export function humanizeNested(value, indent = 0) {
  const pad = "  ".repeat(indent);
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value !== "object") return typeof value === "boolean" ? (value ? "Yes" : "No") : String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.map((item) => nestedLine(pad, "-", item, indent)).join("\n");
  }
  const entries = Object.entries(value);
  if (entries.length === 0) return "—";
  return entries.map(([key, entryValue]) => nestedLine(pad, `${humanizeKey(key)}:`, entryValue, indent)).join("\n");
}

// A row's own identifying field, never the array index (a re-sorted or
// re-filtered page must not silently reattach a key to the wrong row).
const ID_KEY_CANDIDATES = [
  "id", "exch_order_id", "exch_mandate_id", "reg_no", "payment_ref_id", "request_id",
  "client_code", "order_id",
];
export function rowKey(row) {
  if (!isPlainObject(row)) return String(row);
  for (const key of ID_KEY_CANDIDATES) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== "") return `${key}:${value}`;
  }
  const ucc = isPlainObject(row.investor) ? row.investor.ucc : undefined;
  if (ucc) return `ucc:${ucc}:${row.placed_at || row.reg_at || row.created_at || ""}`;
  try {
    return JSON.stringify(row);
  } catch {
    return "row";
  }
}
